/* eslint-disable camelcase */
import { useMutation } from '@tanstack/react-query';

import {
  usePostGenerateUploadUrl,
  usePostMultipartUploadComplete,
  usePostMultipartUploadGenerate,
} from '@/api/rest/resources/file';
import {
  MultipartPart,
  PostMultipartUploadCompleteRequestBodyData,
  PostMultipartUploadGenerateRequestBodyData,
  PresignedUrl,
} from '@/api/rest/resources/types/file';
import { Logger } from '@/lib/logs/logger';
import { assertIsDefined } from '@/utils/assert';

const MULTIPART_UPLOAD_THRESHOLD = 5 * 1024 * 1024; // 5MB
const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB

export const useFileUpload = () => {
  const { submit: submitGenerateMultipartUpload, isSubmitting: isGenerateSubmitting } =
    useGenerateFileMultipartUpload();
  const { submit: submitCompleteMultipartUpload, isSubmitting: isCompleteSubmitting } =
    useCompleteFileMultipartUpload();
  const { uploadFileChunks, isSubmitting: isUploadSubmitting } = useUploadFileToPresignedUrls();
  const { submit: submitGenerateUploadUrl, isSubmitting: isGenerateUploadUrlSubmitting } = useGenerateUploadUrl();

  const submitSmallFile = async (file: File) => {
    const { post_data, file_id } = await submitGenerateUploadUrl(file);
    await uploadFileToPresignedPost(post_data.url, post_data.fields, file);
    return { file_info: file_id, url: undefined };
  };

  const submitLargeFile = async (file: File) => {
    const generateResponse = await submitGenerateMultipartUpload(file);
    const parts = await uploadFileChunks(file, generateResponse.presigned_urls);
    return submitCompleteMultipartUpload(generateResponse.upload_id, parts, generateResponse.file_info);
  };

  const submit = async (file: File, onSuccess?: () => void, onError?: (e: Error) => void) => {
    try {
      const result = file.size < MULTIPART_UPLOAD_THRESHOLD ? await submitSmallFile(file) : await submitLargeFile(file);
      onSuccess?.();
      return result;
    } catch (error) {
      Logger.error(`File upload failed with error:${error}`);
      onError?.(error as Error);
      return {
        file_info: undefined,
        url: undefined,
      };
    }
  };

  const isSubmitting =
    isGenerateUploadUrlSubmitting || isGenerateSubmitting || isUploadSubmitting || isCompleteSubmitting;

  return { submit, isSubmitting, isGenerateSubmitting, isUploadSubmitting, isCompleteSubmitting };
};

export const useGenerateFileMultipartUpload = () => {
  const postMultipartUploadGenerateMutation = usePostMultipartUploadGenerate();

  const submit = async (file: File) => {
    assertIsDefined(file, 'file');

    const fileName = file.name.trim();
    const fileSize = file.size;
    const fileType = file.type.trim();

    assertIsDefined(fileName, 'file.name');
    assertIsDefined(fileSize, 'file.size');
    assertIsDefined(fileType, 'file.type');

    const bodyData: PostMultipartUploadGenerateRequestBodyData = {
      file_size: fileSize,
      file_name: fileName,
      content_type: fileType,
    };

    return postMultipartUploadGenerateMutation.mutateAsync(
      {
        pathVariables: undefined,
        queryParams: undefined,
        bodyData,
      },
      {
        onError: (error) => {
          const message = `Failed to generate multipart upload: ${error}`;
          Logger.error(`${message}. Source: useGenerateFileMultipartUpload`);
        },
      },
    );
  };

  const isSubmitting = postMultipartUploadGenerateMutation.isPending;

  return { submit, isSubmitting };
};

export const useCompleteFileMultipartUpload = () => {
  const postMultipartUploadCompleteMutation = usePostMultipartUploadComplete();

  const submit = async (uploadId: string, parts: MultipartPart[], fileInfo: string) => {
    assertIsDefined(uploadId, 'uploadId');
    assertIsDefined(parts, 'parts');
    assertIsDefined(fileInfo, 'fileInfo');

    if (parts.length === 0) {
      throw new Error('Parts array must not be empty');
    }

    const bodyData: PostMultipartUploadCompleteRequestBodyData = {
      upload_id: uploadId,
      parts,
      file_info: fileInfo,
    };

    return postMultipartUploadCompleteMutation.mutateAsync(
      {
        pathVariables: undefined,
        queryParams: undefined,
        bodyData,
      },
      {
        onError: (error) => {
          const message = `Failed to complete multipart upload: ${error}`;
          Logger.error(`${message}. Source: useCompleteFileMultipartUpload`);
        },
      },
    );
  };

  const isSubmitting = postMultipartUploadCompleteMutation.isPending;

  return { submit, isSubmitting };
};

type UploadFileToPresignedUrlsParams = {
  file: File;
  presignedUrls: PresignedUrl[];
};

export const useUploadFileToPresignedUrls = () => {
  const mutation = useMutation<MultipartPart[], Error, UploadFileToPresignedUrlsParams>({
    mutationFn: async ({ file, presignedUrls }) => {
      assertIsDefined(file, 'file');
      assertIsDefined(presignedUrls, 'presignedUrls');

      if (presignedUrls.length === 0) {
        throw new Error('Presigned URLs array must not be empty');
      }

      const partPromises = presignedUrls.map(async (url) => {
        const start = (url.part_number - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const etag = await uploadChunkToPresignedUrl(url.presigned_url, chunk);
        return { PartNumber: url.part_number, ETag: etag };
      });

      const parts: MultipartPart[] = await Promise.all(partPromises);
      return parts.sort((a, b) => a.PartNumber - b.PartNumber);
    },
  });

  const uploadFileChunks = async (file: File, presignedUrls: PresignedUrl[]) => {
    return mutation.mutateAsync(
      { file, presignedUrls },
      {
        onError: (error) => {
          const message = `Failed to upload file chunk: ${error}`;
          Logger.error(`${message}. Source: uploadFileChunks`);
        },
      },
    );
  };

  const isSubmitting = mutation.isPending;

  return { uploadFileChunks, isSubmitting };
};

const useGenerateUploadUrl = () => {
  const postGenerateUploadUrlMutation = usePostGenerateUploadUrl();

  const submit = async (file: File) => {
    assertIsDefined(file, 'file');

    return postGenerateUploadUrlMutation.mutateAsync(
      {
        pathVariables: undefined,
        queryParams: undefined,
        bodyData: {
          content_type: file.type.trim(),
          name: file.name.trim(),
        },
      },
      {
        onError: (error) => {
          const message = `Failed to generate upload URL: ${error}`;
          Logger.error(`${message}. Source: useGenerateUploadUrl`);
        },
      },
    );
  };

  const isSubmitting = postGenerateUploadUrlMutation.isPending;

  return { submit, isSubmitting };
};

const uploadFileToPresignedPost = async (url: string, fields: Record<string, string>, file: File) => {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
  formData.append('file', file);

  const response = await fetch(url, { method: 'POST', body: formData }).catch((error) => {
    const message = `Failed to upload file to presigned URL: ${error}`;
    Logger.error(`${message}. Source: uploadFileToPresignedPost`);
    throw new Error(message);
  });

  if (!response.ok) {
    throw new Error('Failed to upload file to presigned URL');
  }
};

const uploadChunkToPresignedUrl = async (presignedUrl: string, chunk: Blob) => {
  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    body: chunk,
  }).catch((error) => {
    const message = `Failed to upload chunk to presigned URL: ${error}`;
    Logger.error(`${message}. Source: uploadChunkToPresignedUrl`);
    throw new Error(message);
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload chunk');
  }

  const etag = uploadResponse.headers.get('ETag');
  if (!etag) {
    throw new Error('No ETag received for chunk');
  }

  return etag.replace(/"/g, '');
};
